import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle2 } from "lucide-react";
import CylinderBlueprint from "@/components/CylinderBlueprint";

const DEMO_USERS = [
  { email: "admin@hispl.com", password: "Admin@123", label: "Admin" },
  { email: "sales@hispl.com", password: "Sales@123", label: "Sales & Marketing" },
  { email: "design@hispl.com", password: "Design@123", label: "Design" },
  { email: "purchase@hispl.com", password: "Purchase@123", label: "Purchase & Subcontract" },
  { email: "production@hispl.com", password: "Prod@123", label: "Production" },
  { email: "quality@hispl.com", password: "Quality@123", label: "Quality" },
];

const HIGHLIGHTS = [
  "Complete Cylinder Lifecycle Tracking",
  "Engineering to Dispatch Visibility",
  "Real-Time Manufacturing Intelligence",
];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  function fillDemo(u) { setEmail(u.email); setPassword(u.password); }

  return (
    <div className="min-h-screen flex bg-white" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Left Panel — Navy brand */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden text-white p-14 flex-col justify-between"
           style={{ backgroundColor: "#0B1F3A" }}
           data-testid="login-brand-panel">
        <CylinderBlueprint className="absolute inset-0 w-full h-full" data-testid="login-blueprint" />

        <div className="relative z-10 flex items-center gap-4">
          <div className="w-14 h-14 rounded-lg bg-white p-1.5 shadow-sm flex items-center justify-center">
            <img src="/brand/hispl-logo.jpg" alt="HISPL" className="w-full h-full object-contain" data-testid="brand-logo" />
          </div>
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-300">Hydraulics India Services Pvt Ltd</div>
            <div className="mt-0.5 text-2xl font-bold tracking-wide">HISPL <span className="text-white">NEXUS</span></div>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <div>
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight" data-testid="brand-tagline">
              Engineering Every Cylinder.<br />
              <span style={{ color: "#C62828" }}>Tracking Every Step.</span>
            </h1>
            <p className="mt-4 text-slate-300 text-sm max-w-md">
              A premium Manufacturing Execution System built for the discrete cylinder industry — from customer inquiry to dispatched invoice, one digital passport per unit.
            </p>
          </div>

          <ul className="space-y-3">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="w-4 h-4" style={{ color: "#2E7D32" }} />
                <span className="text-slate-100">{h}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10 flex items-center justify-between text-xs text-slate-400">
          <div>© {new Date().getFullYear()} HYDRAULICS INDIA SERVICES PRIVATE LIMITED</div>
          <div className="flex items-center gap-2 font-mono uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: "#2E7D32" }} />
            HISPL NEXUS v1.0
          </div>
        </div>
      </div>

      {/* Right Panel — Login card */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile brand */}
          <div className="lg:hidden mb-8 text-center">
            <div className="inline-flex items-center gap-3">
              <img src="/brand/hispl-logo.jpg" alt="HISPL" className="w-12 h-12 rounded-lg bg-slate-50 p-1 border border-slate-200" />
              <div className="text-left leading-tight">
                <div className="text-[9px] uppercase tracking-widest text-slate-500">Hydraulics India Services</div>
                <div className="text-xl font-bold text-slate-900">HISPL NEXUS</div>
              </div>
            </div>
          </div>

          <Card className="border border-slate-200 shadow-sm rounded-lg" style={{ backgroundColor: "#F7F8FA" }}>
            <CardContent className="p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-slate-900">Sign in to your account</h2>
                <p className="text-sm text-slate-500 mt-1">Access your MES workspace to continue where you left off.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
                  <Input id="email" type="email" data-testid="login-email" value={email}
                         onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                         placeholder="you@hispl.com"
                         className="mt-1.5 h-11 bg-white border-slate-300 rounded-lg" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Password</Label>
                    <button type="button" className="text-[11px] font-semibold" style={{ color: "#0B1F3A" }}
                            onClick={() => setError("Password reset is not yet enabled — please contact your Admin.")}>
                      Forgot password?
                    </button>
                  </div>
                  <Input id="password" type="password" data-testid="login-password" value={password}
                         onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                         placeholder="••••••••"
                         className="mt-1.5 h-11 bg-white border-slate-300 rounded-lg" />
                </div>

                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                         data-testid="login-remember"
                         className="rounded border-slate-300" style={{ accentColor: "#0B1F3A" }} />
                  Remember me on this device
                </label>

                {error && (
                  <div className="text-xs px-3 py-2 rounded-lg border" data-testid="login-error"
                       style={{ backgroundColor: "#FEF2F2", borderColor: "#FCA5A5", color: "#991B1B" }}>
                    {error}
                  </div>
                )}

                <Button type="submit" disabled={loading} data-testid="login-submit"
                        className="w-full h-11 rounded-lg text-white font-semibold shadow-sm"
                        style={{ backgroundColor: "#C62828" }}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
                </Button>
              </form>

              {/* Green divider — used ONLY as an accent line per brand spec */}
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px" style={{ backgroundColor: "#2E7D32", opacity: 0.35 }} />
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Demo accounts</span>
                <div className="flex-1 h-px" style={{ backgroundColor: "#2E7D32", opacity: 0.35 }} />
              </div>

              <div className="grid grid-cols-2 gap-2">
                {DEMO_USERS.map((u) => (
                  <button key={u.email} type="button" onClick={() => fillDemo(u)}
                          data-testid={`demo-${u.label.toLowerCase().replace(/\s/g, "-")}`}
                          className="text-left px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-slate-400 transition-colors">
                    <div className="text-xs font-semibold text-slate-900">{u.label}</div>
                    <div className="text-[10px] text-slate-500 font-mono truncate">{u.email}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-[11px] text-slate-500">
            Need access? Reach out to your plant admin.
          </p>
        </div>
      </div>
    </div>
  );
}
